import { DTOPK } from '@/shared/api/base-entity';

import { Page, PaginationParams } from '@/shared/api/pagination';
import {
  CreateDashboard,
  Dashboard,
  DashboardShort,
  DashboardTitles,
  UpdateDashboard,
} from '../model/dashboard';
import type { AxiosRequestConfig } from 'axios';

import { srmInstance } from '@/shared/api/api-instances/srm/api-instance';
import type { TQLQuery } from '@/entities/srm/tql-query/@x/dashboard';
import { ImportEntities } from '@/shared/api/import-contracts';

const baseURLOverride = {
  baseURL: '/api/srm/v4',
};

export class DashboardAPI {
  static getDashboardList(
    params?: PaginationParams,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<Page<Dashboard>>(`/dashboard`, {
      ...options,

      params: params,
    });
  }

  static getDashboardListByRuleId(
    rule_id: string,
    params?: PaginationParams,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<Page<Dashboard>>(
      `/dashboards/rule/${rule_id}/dashboards`,
      {
        ...options,
        ...baseURLOverride,
        params: params,
      },
    );
  }

  static getDashboardTitles(options?: AxiosRequestConfig) {
    return srmInstance.get<DashboardTitles[]>('/dashboards/titles', options);
  }

  static getDefaultDashboards(
    ci_type_name: string,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<DashboardShort[]>(
      `/dashboards/default/${ci_type_name}`,
      options,
    );
  }

  static getDashboard(dashboardName: string, options?: AxiosRequestConfig) {
    return srmInstance.get<Dashboard>(`/dashboard/${dashboardName}`, {
      ...baseURLOverride,
      ...options,
    });
  }

  static getDashboardById(id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<Dashboard>(`/dashboard/${id}`, {
      ...options,
    });
  }
  static updateDashboard(
    dashboard: UpdateDashboard,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<Dashboard>(
      `/dashboard/${dashboard.name}`,
      dashboard,
      {
        ...baseURLOverride,
        ...options,
      },
    );
  }

  static deleteDashboard(
    dashboardName: DTOPK<Dashboard>,
    force_flag?: boolean,
    params?: object,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.delete(`/dashboard/${dashboardName}`, {
      ...options,
      ...baseURLOverride,
      params: {
        ...params,
        force: force_flag,
      },
    });
  }

  static createDashboard(
    dashboard: CreateDashboard,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<Dashboard>('/dashboard', dashboard, {
      ...baseURLOverride,
      ...options,
    });
  }

  static cloneDashboard(dashboardName: string, options?: AxiosRequestConfig) {
    return srmInstance.post(`/dashboard/${dashboardName}/clone`, undefined, {
      ...baseURLOverride,
      ...options,
    });
  }

  static exportDashboards(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<{ dashbords: Dashboard[]; queries: TQLQuery[] }>(
      '/export/dashboards/json',
      ids,
      options,
    );
  }

  static exportFileDashboards(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post('/export/dashboards/json_file', ids, options);
  }

  static importFileDashboards(
    formData: FormData,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<string>(
      '/import/dashboards/json_file',
      formData,
      options,
    );
  }

  static importJSONDashboards(json: Dashboard, options?: AxiosRequestConfig) {
    return srmInstance.post<string>('/import/dashboards/json', json, options);
  }

  static analyzeDashboardEntities(
    json: Dashboard,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/dashboards/analyze', json, {
      ...baseURLOverride,
      ...options,
    });
  }

  static executeDashboardEntities(
    data: ImportEntities,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/dashboards/execute', data, {
      ...baseURLOverride,
      ...options,
    });
  }

  static validateImport(
    json: { dashbords: Dashboard[]; queries: TQLQuery[] },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/dashboards/validate', json, {
      ...baseURLOverride,
      ...options,
    });
  }
}